import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { of, switchMap, take } from 'rxjs';
import {
    ACCESS_DENIED_PATH,
    createAccessDeniedUrlTree,
} from 'app/core/auth/utils/access-denied.util';
import { NavigationCacheService } from 'app/core/navigation/navigation-cache.service';

/**
 * Routes that bypass the RoutePermissionGuard check.
 * Add route patterns here to skip permission validation.
 */
const SKIPPED_ROUTE_PERMISSION_GUARD_ROUTES: string[] = [
  '/assistant/delivery/loadsV3',
  '/home',
  '/inventory/material-inventory',
  '/inventory/material-movement',
  '/reports/historic/pack-loan-request-report',
  '/reports/historic/quotation-customer',
  '/menu-management',
  '/assistant/sales/distribution-v3',
  '/master/routing/territory-customers-map',
  ACCESS_DENIED_PATH,
];

/**
 * Global Route Permission Guard
 *
 * Validates that user can only access routes that exist in their navigation menu.
 * Uses NavigationCacheService which subscribes to NavigationService (single source of truth).
 *
 * Features:
 * - Ensures navigation is loaded from API before validating (calls ensureNavigationLoaded())
 * - O(1) route lookup using Set<string>
 * - Blocks route activation BEFORE rendering any component
 * - Redirects to /access-denied if access denied (with attemptedUrl query)
 * - Works with canActivate and canActivateChild for automatic application to child routes
 * - Supports skipped routes via SKIPPED_ROUTE_PERMISSION_GUARD_ROUTES constant
 *
 * Flow:
 * 1. Guard executes
 * 2. Calls ensureNavigationLoaded() which:
 *    - If data exists → returns immediately
 *    - If no data → calls NavigationService.get() to fetch from API
 * 3. Once loaded, validates route permission
 * 4. If allowed → return true (allow navigation)
 * 5. If not allowed → return UrlTree to access-denied (redirect without rendering)
 *
 * Usage:
 * Add to parent routes with canActivateChild to automatically protect all child routes:
 * {
 *   path: 'master',
 *   canActivate: [AuthGuard, RoutePermissionGuard],
 *   canActivateChild: [AuthGuard, RoutePermissionGuard],
 *   children: [ ... ] // All children automatically protected
 * }
 */
export const RoutePermissionGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const navigationCache = inject(NavigationCacheService);
    const router = inject(Router);


    // Ensure navigation is loaded (calls API if needed, or returns immediately if cached)
    return navigationCache.ensureNavigationLoaded().pipe(
        switchMap((loaded) => {
            // If failed to load navigation, redirect to home
            if (!loaded) {
                return of(router.parseUrl('/home'));
            }

            const targetUrl = state.url;

            // Check if route should skip permission validation
            const isSkippedRoute = SKIPPED_ROUTE_PERMISSION_GUARD_ROUTES.some(
                route => targetUrl.includes(route)
            );
            if (isSkippedRoute) {
                return of(true);
            }

            // Check if route is allowed (O(1) lookup with Set.has())
            const isAllowed = navigationCache.isRouteAllowed(targetUrl);

            if (isAllowed) {
                return of(true); // Allow navigation
            }


            // Access denied - redirect to access-denied page with context
            return of(createAccessDeniedUrlTree(router, targetUrl));
        }),
        take(1) // Complete immediately after check
    );
};
